(() => {
    if (customElements.get('po-minicart-shop-accessories')) return;

    class PoMinicartShopAccessories extends HTMLElement {
        connectedCallback() {
            this._aborted = false;
            this._hasTrackedRecommendationView = false;
            this._hasSentRecommendationAlgoliaView = false;
            this._recommendationViewRetryCount = 0;
            this._recommendationViewRetryTimer = null;
            this._recommendationItems = [];
            this.collectionHandle = this.dataset.collectionHandle || '';
            this.ruleContext = this.dataset.ruleContext || '';
            this.collectionView = this.dataset.collectionView || 'po-minicart-shop-accessories-fragment';
            this.itemView = this.dataset.itemView || 'po-minicart-shop-accessories';
            this.limit = Math.max(1, parseInt(this.dataset.limit || '12', 10));
            this.page = Math.max(1, parseInt(this.dataset.page || '1', 10));
            this.setupDeferredRender();
            this.renderIfMiniCartOpen();
        }

        disconnectedCallback() {
            this._aborted = true;
            this._deferredRenderSetup = false;

            if (this._notificationObserver) {
                this._notificationObserver.disconnect();
                this._notificationObserver = null;
            }

            if (this._onCartItemAdded) {
                document.removeEventListener('po:cart:item-added', this._onCartItemAdded);
                this._onCartItemAdded = null;
            }

            if (typeof this._unsubscribeCartUpdate === 'function') {
                this._unsubscribeCartUpdate();
                this._unsubscribeCartUpdate = null;
            }

            if (this._mql && this._onMqlChange) {
                this._mql.removeEventListener('change', this._onMqlChange);
                this._onMqlChange = null;
                this._mql = null;
            }

            this.disconnectRecommendationTracking();
        }

        setupDeferredRender() {
            if (this._deferredRenderSetup) return;
            this._deferredRenderSetup = true;

            this._onCartItemAdded = () => {
                this.render();
            };
            document.addEventListener('po:cart:item-added', this._onCartItemAdded);

            const cartUpdateEvent =
                typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS?.cartUpdate : 'cart-update';

            if (typeof window.subscribe === 'function') {
                this._unsubscribeCartUpdate = window.subscribe(cartUpdateEvent, (event = {}) => {
                    const source = event?.source;
                    if (source === 'product-form' || source === 'quick-add') {
                        this.render();
                    }
                });
            }

            const notification = document.getElementById('cart-notification');
            if (!notification) return;

            this._notificationObserver = new MutationObserver(() => {
                if (notification.classList.contains('active')) {
                    this.render();
                }
            });

            this._notificationObserver.observe(notification, {
                attributes: true,
                attributeFilter: ['class'],
            });
        }

        renderIfMiniCartOpen() {
            const notification = document.getElementById('cart-notification');
            if (notification?.classList.contains('active')) {
                this.render();
            }
        }

        _log(...args) {
            console.error('[po-minicart-shop-accessories]', ...args);
        }

        _skeleton() {
            this.innerHTML = `
        <div class="tw-relative tw-flex tw-h-full">
          <div class="tw-flex tw-gap-po-spacings-base tw-items-center">
            <div class="tw-animate-pulse tw-h-24 tw-w-24 tw-bg-po-secondary tw-rounded"></div>
            <div class="tw-animate-pulse tw-h-4 tw-w-40 tw-bg-po-secondary tw-rounded"></div>
          </div>
        </div>
      `;
        }

        async render() {
            if (this._aborted || this._rendering) return;
            this._rendering = true;
            this._skeleton();

            try {
                const fastHTML = await this.fetchCollectionFragment();
                if (this._aborted) return;

                if (fastHTML && fastHTML.trim()) {
                    this.renderCardsFromHTML(fastHTML);
                    this.bindAddToCartHandlers(this);
                    this._rendered = true;
                    return;
                }

                this._log('collection fragment empty/failure, using Algolia fallback…');
                let items = await this.fetchCollectionHandles();
                if (!items.length && this.ruleContext && this.ruleContext !== 'CART') {
                    items = await this.fetchCollectionHandles('CART');
                }
                if (!items.length) {
                    this.innerHTML = '';
                    return;
                }

                const cards = await this.fetchFragmentsForHandles(items, 2);
                if (!cards.length) {
                    this.innerHTML = '';
                    return;
                }

                this.renderCardsFromList(cards);
                this.bindAddToCartHandlers(this);
                this._rendered = true;
            } catch (e) {
                this._log('render error', e);
            } finally {
                this._rendering = false;
            }
        }

        async fetchCollectionFragment() {
            if (!this.collectionHandle) {
                return '';
            }

            const url =
                `/collections/${encodeURIComponent(this.collectionHandle)}` +
                `?view=${encodeURIComponent(this.collectionView)}`;
            try {
                const res = await fetch(url, {credentials: 'same-origin', headers: {'X-Requested-With': 'fetch'}});
                if (!res.ok) return '';
                const html = await res.text();
                return this.limitTrim(html, this.limit);
            } catch (e) {
                this._log('fetchCollectionFragment error', e);
                return '';
            }
        }

        async fetchCollectionHandles(ruleContext = this.ruleContext) {
            const helper = window.poAlgoliaFetchCollectionHandles;
            if (typeof helper === 'function') {
                return helper({
                    collectionHandle: this.collectionHandle,
                    limit: this.limit,
                    ruleContext,
                    includeMetadata: true,
                    includeCollectionHandleInRuleContexts: false,
                    logPrefix: '[po-minicart-shop-accessories]',
                    errorLabel: 'fetchCollectionHandles Algolia error',
                });
            }

            this._log('Helper poAlgoliaFetchCollectionHandles mancante');
            return [];
        }

        limitTrim(html, limit) {
            try {
                const div = document.createElement('div');
                div.innerHTML = html;
                const all = [...div.querySelectorAll('.po-minicart-accessory-card')];
                if (all.length <= limit) return html;
                for (let i = limit; i < all.length; i++) all[i].remove();
                return div.innerHTML;
            } catch {
                return html;
            }
        }

        renderCardsFromHTML(html) {
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            const cards = [...tmp.querySelectorAll('.po-minicart-accessory-card')].map(n => n.outerHTML);
            this.renderCardsFromList(cards);
        }

        renderCardsFromList(cards) {
            this._cards = cards;
            this._hasTrackedRecommendationView = false;
            this._hasSentRecommendationAlgoliaView = false;
            this._recommendationViewRetryCount = 0;
            this._recommendationItems = [];
            this.disconnectRecommendationTracking();

            if (!this._mql) {
                this._mql = window.matchMedia('(max-width: 1023px)');
                this._onMqlChange = () => this.applyResponsiveLayout();
                this._mql.addEventListener('change', this._onMqlChange);
            }

            this.applyResponsiveLayout();
        }

        applyResponsiveLayout() {
            if (!this._cards) return;

            if (this._mql.matches) {
                this.buildMobileSlider(this._cards);
            } else {
                this.buildDesktopColumn(this._cards);
            }

            this.dispatchEvent(new CustomEvent('po:swiper:ready', {bubbles: true}));
            this.setupRecommendationTracking();
        }

        buildDesktopColumn(cards) {
            if (this._swiper && typeof this._swiper.destroy === 'function') {
                this._swiper.destroy(true, true);
                this._swiper = null;
            }

            this.innerHTML = `
    <div class="po-minicart-accessories--column tw-flex tw-flex-col tw-gap-16"></div>
  `;

            const col = this.querySelector('.po-minicart-accessories--column');
            col.innerHTML = '';

            cards.forEach((html) => {
                const node = document.createElement('div');
                node.className = 'po-minicart-accessory-row';
                node.innerHTML = html;

                col.appendChild(node);
            });
        }

        buildMobileSlider(cards) {
            this.innerHTML = `
    <div class="swiper">
      <div class="swiper-wrapper"></div>
      <div class="swiper-scrollbar tw-hidden sd:tw-block"></div>
      <div class="sd:tw-hidden po-swiper-paginator tw-relative tw-mt-po-spacings-lg tw-text-center [&>.swiper-pagination-bullet]:tw-border-white [&>.swiper-pagination-bullet-active]:tw-bg-white"></div>
    </div>
  `;

            const wrapper = this.querySelector('.swiper-wrapper');
            wrapper.innerHTML = '';

            cards.forEach((html) => {
                const slide = document.createElement('div');
                slide.className = 'swiper-slide';
                slide.innerHTML = html;

                const vId = slide.querySelector('[data-variant-id]')?.getAttribute('data-variant-id');
                if (vId) slide.setAttribute('data-variant-id', vId);

                wrapper.appendChild(slide);
            });

            this.initMobileSwiper();
        }

        initMobileSwiper() {
            if (this._swiper && typeof this._swiper.destroy === 'function') {
                this._swiper.destroy(true, true);
                this._swiper = null;
            }

            const container = this.querySelector('.swiper');
            if (window.Swiper) {
                this._swiper = new window.Swiper(container, {
                    slidesPerView: 1.08,
                    spaceBetween: 8,
                    slidesOffsetAfter: 24,
                    grabCursor: true,
                    observeParents: true,
                    observer: true,
                    watchSlidesProgress: true,
                    scrollbar: {el: this.querySelector('.swiper-scrollbar'), draggable: true},
                    breakpoints: {
                        480: {slidesPerView: 1.08, spaceBetween: 8},
                        640: {slidesPerView: 1.35, spaceBetween: 16},
                        768: {slidesPerView: 2, spaceBetween: 16, slidesOffsetAfter: 0},
                        900: {slidesPerView: 2, spaceBetween: 16},
                    },
                    pagination: {
                        el: this.querySelector('.po-swiper-paginator'),
                        type: 'bullets',
                        clickable: true
                    }
                });
                return;
            }

            const wrapper = this.querySelector('.swiper-wrapper');
            container.style.overflowX = 'auto';
            container.style.scrollSnapType = 'x mandatory';
            container.style.WebkitOverflowScrolling = 'touch';
            wrapper.style.display = 'flex';
            wrapper.style.flexWrap = 'nowrap';
            wrapper.style.gap = '12px';
            wrapper.querySelectorAll('.swiper-slide').forEach((s) => {
                s.style.flex = '0 0 85%';
                s.style.scrollSnapAlign = 'start';
            });
        }

        disconnectRecommendationTracking() {
            if (this._recommendationViewObserver) {
                this._recommendationViewObserver.disconnect();
                this._recommendationViewObserver = null;
            }

            if (this._recommendationClickHandler) {
                this.removeEventListener('click', this._recommendationClickHandler, true);
                this._recommendationClickHandler = null;
            }

            if (this._recommendationViewRetryTimer) {
                window.clearTimeout(this._recommendationViewRetryTimer);
                this._recommendationViewRetryTimer = null;
            }
        }

        isRecommendationVisible() {
            if (!this.isConnected) return false;

            const notification = document.getElementById('cart-notification');
            if (notification && !notification.classList.contains('active')) return false;

            const style = window.getComputedStyle(this);
            if (style.display === 'none' || style.visibility === 'hidden') return false;

            const rect = this.getBoundingClientRect();
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

            return (
                rect.width > 0 &&
                rect.height > 0 &&
                rect.bottom > 0 &&
                rect.right > 0 &&
                rect.top < viewportHeight &&
                rect.left < viewportWidth
            );
        }

        getHandleFromUrl(url) {
            try {
                const parsedUrl = new URL(url, window.location.origin);
                const match = parsedUrl.pathname.match(/\/products\/([^/?#]+)/);
                return match?.[1] || '';
            } catch (_error) {
                return '';
            }
        }

        getObjectIdFromElement(element) {
            let currentElement = element instanceof Element ? element : null;

            while (currentElement) {
                const dataset = currentElement.dataset || {};
                const objectId =
                    dataset.algoliaObjectId ||
                    dataset.variantId ||
                    dataset.laDnProductVariant ||
                    '';

                if (objectId) {
                    return String(objectId).trim();
                }

                currentElement = currentElement.parentElement;
            }

            return '';
        }

        getRecommendationEventName(action) {
            if (typeof window.poAlgoliaInsights?.getEventName === 'function') {
                return window.poAlgoliaInsights.getEventName(action, 'complementary');
            }

            if (action === 'view') return 'Frequently Bought Together Viewed';
            if (action === 'click') return 'Frequently Bought Together Clicked';
            if (action === 'addToCart') return 'Frequently Bought Together Added To Cart';
            return 'Recommendation Event';
        }

        collectRecommendationItems() {
            const links = Array.from(this.querySelectorAll('a[href*="/products/"]'));
            const seenKeys = new Set();
            const items = [];

            links.forEach((link) => {
                const href = link.getAttribute('href');
                if (!href) return;

                const url = new URL(href, window.location.origin).toString();
                const handle = this.getHandleFromUrl(url);
                const trackedItem = typeof window.poAlgoliaInsights?.getItemFromElement === 'function'
                    ? window.poAlgoliaInsights.getItemFromElement(link, {
                        objectID: this.getObjectIdFromElement(link),
                        handle,
                        url,
                        position: items.length + 1,
                        source: 'complementary',
                    })
                    : {
                        objectID: this.getObjectIdFromElement(link),
                        handle,
                        url,
                        position: items.length + 1,
                        source: 'complementary',
                    };
                const key = trackedItem.objectID || handle || url;

                if (seenKeys.has(key)) return;
                seenKeys.add(key);

                items.push({
                    ...trackedItem,
                    handle,
                    url,
                    position: trackedItem.position || items.length + 1,
                });
            });

            return items;
        }

        sendRecommendationViewToAlgolia() {
            if (this._hasSentRecommendationAlgoliaView || !this._recommendationItems.length) return;

            if (typeof window.poAlgoliaInsights?.trackViewedItems === 'function') {
                const latestItems = this.collectRecommendationItems();
                if (latestItems.length) {
                    this._recommendationItems = latestItems;
                }

                this._hasSentRecommendationAlgoliaView = true;
                window.poAlgoliaInsights.trackViewedItems({
                    source: 'complementary',
                    eventName: this.getRecommendationEventName('view'),
                    items: this._recommendationItems,
                });
                return;
            }

            if (this._recommendationViewRetryTimer || this._recommendationViewRetryCount >= 20) return;

            this._recommendationViewRetryCount += 1;
            this._recommendationViewRetryTimer = window.setTimeout(() => {
                this._recommendationViewRetryTimer = null;
                this.sendRecommendationViewToAlgolia();
            }, 100);
        }

        trackRecommendationView() {
            if (this._hasTrackedRecommendationView || !this._recommendationItems.length) return;

            this._hasTrackedRecommendationView = true;
            const detail = {
                source: 'complementary',
                eventName: this.getRecommendationEventName('view'),
                sectionId: this.dataset.sectionId || 'cart-notification',
                productId: this.dataset.productId || '',
                items: this._recommendationItems.map(({handle, url, position, objectID, queryID, indexName}) => ({
                    handle,
                    url,
                    position,
                    objectID,
                    queryID,
                    indexName,
                })),
            };

            document.dispatchEvent(new CustomEvent('po:recommendations:view', {detail}));
            this.sendRecommendationViewToAlgolia();

            const recommendationViewPayload = {
                id: 'Event',
                Events_RecommendationView: '1',
                Recommendation_EventName: detail.eventName,
                Recommendation_Type: detail.source,
                Recommendation_SourceProductId: detail.productId,
                Recommendation_Items: detail.items.map((item) => item.handle || item.url).join('|'),
            };

            if (typeof window.sendTealiumEvent === 'function') {
                window.sendTealiumEvent(recommendationViewPayload);
            } else if (typeof window.tealium_data2track?.push === 'function') {
                window.tealium_data2track.push({ ...recommendationViewPayload });
            }
        }

        trackRecommendationClick(item) {
            if (!item) return;

            const detail = {
                source: 'complementary',
                eventName: this.getRecommendationEventName('click'),
                sectionId: this.dataset.sectionId || 'cart-notification',
                productId: this.dataset.productId || '',
                item,
            };

            document.dispatchEvent(new CustomEvent('po:recommendations:click', {detail}));

            if (typeof window.poAlgoliaInsights?.trackClickedItem === 'function') {
                window.poAlgoliaInsights.trackClickedItem({
                    source: detail.source,
                    eventName: detail.eventName,
                    item,
                });
            }
        }

        setupRecommendationTracking() {
            this.disconnectRecommendationTracking();
            this._recommendationItems = this.collectRecommendationItems();

            if (!this._recommendationItems.length) return;

            this._recommendationClickHandler = (event) => {
                const link = event.target?.closest('a[href]');
                if (!link || !this.contains(link)) return;

                const href = link.getAttribute('href');
                if (!href || !href.includes('/products/')) return;

                const url = new URL(href, window.location.origin).toString();
                const handle = this.getHandleFromUrl(url);
                const trackedItem = typeof window.poAlgoliaInsights?.getItemFromElement === 'function'
                    ? window.poAlgoliaInsights.getItemFromElement(link, {
                        objectID: this.getObjectIdFromElement(link),
                        handle,
                        url,
                        source: 'complementary',
                    })
                    : {
                        objectID: this.getObjectIdFromElement(link),
                        handle,
                        url,
                        source: 'complementary',
                    };
                const item = {
                    ...trackedItem,
                    handle,
                    url,
                    position:
                        trackedItem.position
                        || this._recommendationItems.find((entry) => {
                            return (
                                (entry.objectID && entry.objectID === trackedItem.objectID)
                                || (entry.handle && entry.handle === handle)
                                || entry.url === url
                            );
                        })?.position
                        || '',
                };

                this.trackRecommendationClick(item);
            };

            this.addEventListener('click', this._recommendationClickHandler, true);

            if (typeof IntersectionObserver !== 'function') {
                this.trackRecommendationView();
                return;
            }

            this._recommendationViewObserver = new IntersectionObserver((entries, observer) => {
                if (!entries[0].isIntersecting) return;
                observer.disconnect();
                this._recommendationViewObserver = null;
                this.trackRecommendationView();
            }, {threshold: 0.2});

            this._recommendationViewObserver.observe(this);

            window.requestAnimationFrame(() => {
                if (this.isRecommendationVisible()) {
                    this.trackRecommendationView();
                }
            });
        }

        async fetchFragmentsForHandles(items, concurrency = 2) {
            const queue = Array.isArray(items) ? items.map((item, index) => ({item, index})) : [];
            const results = new Array(queue.length).fill('');
            const worker = async () => {
                while (queue.length) {
                    const nextItem = queue.shift();
                    if (!nextItem) break;
                    const html = await this.fetchProductItemFragment(nextItem.item);
                    if (html && html.trim()) {
                        results[nextItem.index] = html;
                    }
                }
            };
            await Promise.all(Array.from({length: Math.min(concurrency, queue.length)}, worker));
            return results.filter(Boolean);
        }

        annotateAlgoliaTracking(root, item) {
            if (!(root instanceof Element) || typeof window.poAlgoliaInsights?.annotateElement !== 'function') {
                return;
            }

            const algoliaItem = {
                ...item,
                source: 'complementary',
            };
            const targets = [
                root,
                ...root.querySelectorAll(
                    'a[href*="/products/"], product-form, .po-minicart-accessory-card, .card-product--info, [type="submit"], .js-add-to-cart'
                ),
            ];

            targets.forEach((target) => window.poAlgoliaInsights.annotateElement(target, algoliaItem));
        }

        applyAlgoliaTrackingToFragment(html, item) {
            if (!html || !item) {
                return html;
            }

            const wrapper = document.createElement('div');
            wrapper.innerHTML = html;
            const card = wrapper.firstElementChild || wrapper;
            this.annotateAlgoliaTracking(card, item);
            return wrapper.innerHTML;
        }

        async fetchProductItemFragment(item) {
            const handle = typeof item === 'string' ? item : item?.handle;
            const view = this.itemView || 'po-minicart-shop-accessories';
            const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
            const url = `${root}products/${encodeURIComponent(handle)}?view=${encodeURIComponent(view)}`;
            const cacheKey = `${handle}|${view}|po-minicart-accessory`;
            const cache = window.poAlgoliaCache?.cards;

            if (cache?.has(cacheKey)) {
                const rawHtml = await cache.get(cacheKey);
                return this.applyAlgoliaTrackingToFragment(rawHtml, item);
            }

            const promise = (async () => {
                try {
                    const res = await fetch(url, {
                        credentials: 'same-origin',
                        headers: {'X-Requested-With': 'fetch'},
                    });
                    if (!res.ok) {
                        this._log('fetchProductItemFragment non-OK', url, res.status);
                        return '';
                    }

                    const text = await res.text();
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(text, 'text/html');

                    const card = doc.querySelector('div[id^="cart-notification-product-"]');
                    if (!card) {
                        this._log('fetchProductItemFragment: nessun cart-notification-product-* trovato per', handle);
                        return '';
                    }

                    const match = card.id.match(/cart-notification-product-(\d+)/);
                    const variantId = match ? match[1] : '';

                    const wrapper = document.createElement('div');
                    wrapper.className = 'po-minicart-accessory-card tw-flex tw-h-full';
                    if (variantId) wrapper.setAttribute('data-variant-id', variantId);
                    wrapper.innerHTML = card.outerHTML;

                    return wrapper.outerHTML;
                } catch (e) {
                    this._log('fetchProductItemFragment error', handle, e);
                    return '';
                }
            })();

            if (cache) {
                const pending = promise.then((html) => {
                    if (!html) {
                        cache.delete(cacheKey);
                        return '';
                    }
                    cache.set(cacheKey, Promise.resolve(html));
                    return html;
                });
                cache.set(cacheKey, pending);
                const rawHtml = await pending;
                return this.applyAlgoliaTrackingToFragment(rawHtml, item);
            }

            const rawHtml = await promise;
            return this.applyAlgoliaTrackingToFragment(rawHtml, item);
        }

        bindAddToCartHandlers(root) {
            if (root.dataset.poMinicartAtcBound === 'true') return;
            root.dataset.poMinicartAtcBound = 'true';

            const getVariantId = (el) =>
                el.getAttribute('data-variant-id') ||
                el.closest('[data-variant-id]')?.getAttribute('data-variant-id');

            const click = async (btn) => {
                const id = getVariantId(btn);
                const quantity = parseInt(btn.getAttribute('data-quantity') || '1', 10);
                if (!id) {
                    this._log('missing variant id on add-to-cart');
                    return;
                }
                const algoliaItem = typeof window.poAlgoliaInsights?.getItemFromElement === 'function'
                    ? window.poAlgoliaInsights.getItemFromElement(btn, {
                        objectID: String(id),
                        quantity,
                        source: 'complementary',
                    })
                    : {
                        objectID: String(id),
                        quantity,
                        source: 'complementary',
                    };

                const cartNotification = document.querySelector('cart-notification');
                const sectionsToRender = cartNotification
                    ? cartNotification.getSectionsToRender().map((s) => s.id)
                    : [];

                btn.disabled = true;
                try {
                    const res = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        },
                        credentials: 'same-origin',
                        body: JSON.stringify({
                            id: Number(id),
                            quantity,
                            properties: {
                                _status: 'Available',
                                _lens_type: 'PLANO',
                            },
                            sections: sectionsToRender,
                            sections_url: window.location.pathname,
                        }),
                    });
                    if (!res.ok) throw new Error(await res.text());

                    const responseJson = await res.json();

                    if (cartNotification) {
                        cartNotification.renderContents(responseJson);
                    } else {
                        this.openMiniCartDrawer();
                    }

                    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                        publish(PUB_SUB_EVENTS.cartUpdate, {
                            source: 'quick-add',
                            productVariantId: Number(id),
                            cartData: responseJson,
                        });
                    }

                    if (typeof window.poAlgoliaInsights?.trackAddedToCart === 'function') {
                        window.poAlgoliaInsights.trackAddedToCart({
                            source: algoliaItem.source || 'complementary',
                            items: [
                                {
                                    ...algoliaItem,
                                    objectID: algoliaItem.objectID || String(id),
                                    quantity,
                                },
                            ],
                        });
                    }

                    document.dispatchEvent(new CustomEvent('po:cart:item-added', {
                        detail: {
                            source: 'quick-add',
                            variantId: Number(id),
                            quantity,
                            cartData: responseJson,
                        }
                    }));

                    if (window.innerWidth < 1024) {
                        const scrollContainer = document.querySelector('.po-minicart-container');
                        if (scrollContainer) {
                            scrollContainer.scrollTo({top: 0, behavior: 'smooth'});
                        }
                    }
                } catch (e) {
                    this._log('add to cart failed', e);
                } finally {
                    btn.disabled = false;
                }
            };

            root.addEventListener('click', (e) => {
                const el = e.target.closest('.js-add-to-cart');
                if (el) {
                    e.preventDefault();
                    click(el);
                }
            }, {passive: false});
        }

        openMiniCartDrawer() {
            const drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.open === 'function') return drawer.open();
            document.dispatchEvent(new CustomEvent('po:cart:open', {bubbles: true}));
            const trigger = document.querySelector('[data-open-cart-drawer]');
            if (trigger) {
                trigger.click();
                return true;
            }
            return false;
        }
    }

    customElements.define('po-minicart-shop-accessories', PoMinicartShopAccessories);
})();
